'use client';

/**
 * SVG icons stilizzati per le categorie esperienze.
 * Outline 1.5px stroke, viewBox 24, currentColor → eredita text-color.
 */

interface Props {
  id: string;
  size?: number;
  className?: string;
}

export default function CategoryIcon({ id, size = 48, className = '' }: Props) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };

  switch (id) {
    case 'pescaturismo':
      // Canna da pesca + amo
      return (
        <svg {...svgProps}>
          <path d="M3 4l9 7" />
          <path d="M12 11l4 3" />
          <path d="M16 14a3 3 0 1 1-3 3" />
          <circle cx="12" cy="20" r="1" fill="currentColor" />
        </svg>
      );

    case 'tour-barca':
      // Barca a vela
      return (
        <svg {...svgProps}>
          <path d="M12 3v13" />
          <path d="M12 5l-5 11h10z" />
          <path d="M3 19c1 1 2 1 3 0s2-1 3 0 2 1 3 0 2-1 3 0 2 1 3 0" />
          <path d="M3 19c1 1 2 1 3 0s2-1 3 0 2 1 3 0 2-1 3 0 2 1 3 0" transform="translate(0 2)" opacity="0.5" />
        </svg>
      );

    case 'sunset-cruise':
      // Sole che tramonta sul mare
      return (
        <svg {...svgProps}>
          <line x1="12" y1="10" x2="12" y2="3" />
          <line x1="5" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="19" y2="12" />
          <line x1="7" y1="7" x2="5.5" y2="5.5" />
          <line x1="17" y1="7" x2="18.5" y2="5.5" />
          <path d="M6 15a6 6 0 0 1 12 0H6z" />
          <path d="M2 18h20" />
          <path d="M2 21c1.5-1 3 1 5 0s3 1 5 0 3 1 5 0 3 1 3 1" opacity="0.6" />
        </svg>
      );

    case 'grotte-marine':
      // Arco/grotta + onda
      return (
        <svg {...svgProps}>
          <path d="M4 20V12a8 8 0 0 1 16 0v8" />
          <path d="M4 20h16" />
          <path d="M8 20v-4a4 4 0 0 1 8 0v4" />
          <path d="M2 21c1.5-1 3 1 5 0s3 1 5 0 3 1 5 0 3 1 3 1" opacity="0.5" />
        </svg>
      );

    case 'tour-isole':
      // 3 isole con palma + onde
      return (
        <svg {...svgProps}>
          <path d="M3 17c1-2 4-2 5 0" />
          <path d="M9 16c1.5-3 5-3 6 0" />
          <path d="M16 17c.8-1.5 3-1.5 4 0" />
          <path d="M12 14V9" />
          <path d="M12 9c-2-1-3 0-3 0" />
          <path d="M12 9c2-1 3 0 3 0" />
          <path d="M12 9c-1-1.5 0-3 0-3" />
          <path d="M2 20c1.5-1 3 1 5 0s3 1 5 0 3 1 5 0 3 1 3 1" opacity="0.5" />
        </svg>
      );

    case 'pesca-sportiva':
      // Pesce con esca
      return (
        <svg {...svgProps}>
          <path d="M6.5 12c0-2.5 3-5 7-5s5 2 5 5-1 5-5 5-7-2.5-7-5z" />
          <path d="M18.5 12c1-1.5 2.5-2 2.5-2v4s-1.5-.5-2.5-2z" />
          <circle cx="16" cy="11" r="0.8" fill="currentColor" />
          <path d="M6.5 12c-1-1.5-2-2-3-2s-1 1-1 2 0 2 1 2 2-.5 3-2z" />
        </svg>
      );

    case 'charter-custom':
      // Bussola
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7l2.5 4.5L12 17l-2.5-5.5z" fill="currentColor" fillOpacity="0.15" />
          <circle cx="12" cy="12" r="1" fill="currentColor" />
          <line x1="12" y1="3" x2="12" y2="4.5" />
          <line x1="12" y1="19.5" x2="12" y2="21" />
          <line x1="3" y1="12" x2="4.5" y2="12" />
          <line x1="19.5" y1="12" x2="21" y2="12" />
        </svg>
      );

    case 'sport-acquatici':
      // Onda + figura stilizzata su tavola
      return (
        <svg {...svgProps}>
          <path d="M3 17c2 0 3-1.2 4.5-1.2S10 17 12 17s3-1.2 4.5-1.2S19 17 21 17" />
          <path d="M3 20c2 0 3-1.2 4.5-1.2S10 20 12 20s3-1.2 4.5-1.2S19 20 21 20" opacity="0.55" />
          <ellipse cx="13" cy="10.5" rx="6" ry="1.4" transform="rotate(-18 13 10.5)" />
          <circle cx="15.5" cy="6" r="1.7" />
          <path d="M14 8l-1 4.5" />
        </svg>
      );

    case 'snorkeling':
      // Maschera snorkel + boccaglio
      return (
        <svg {...svgProps}>
          <path d="M5 9h11a2 2 0 0 1 2 2v3a3 3 0 0 1-3 3h-1a3 3 0 0 1-3-3" />
          <path d="M9 14a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-3a2 2 0 0 1 2-2" />
          <path d="M9 14h3" />
          <path d="M18 11h2v-7" />
          <path d="M20 4a1.2 1.2 0 1 0 0 .01" />
        </svg>
      );

    case 'chef-on-board':
      // Cappello chef
      return (
        <svg {...svgProps}>
          <path d="M6 14V9a4 4 0 0 1 4-4 4 4 0 0 1 4 0 4 4 0 0 1 4 4v5" />
          <path d="M5 14h14v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-3z" />
          <path d="M9 17h6" />
        </svg>
      );

    case 'festa-privata':
      // Calice + bolle
      return (
        <svg {...svgProps}>
          <path d="M8 4h8l-1 7a4 4 0 0 1-3 3 4 4 0 0 1-3-3z" />
          <path d="M12 14v6" />
          <path d="M9 20h6" />
          <circle cx="5" cy="6" r="0.6" fill="currentColor" />
          <circle cx="19" cy="9" r="0.6" fill="currentColor" />
          <circle cx="4" cy="11" r="0.5" fill="currentColor" />
          <circle cx="20" cy="4" r="0.5" fill="currentColor" />
        </svg>
      );

    case 'wellness':
      // Figura yoga / loto
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="6" r="2" />
          <path d="M12 8v4" />
          <path d="M12 12c-3 0-5 2-5 5h10c0-3-2-5-5-5z" />
          <path d="M6 16c-1.5 0-3 1-3 2h6" />
          <path d="M18 16c1.5 0 3 1 3 2h-6" />
        </svg>
      );

    default:
      // Fallback: cerchio + punto
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
        </svg>
      );
  }
}
